import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DOCUMENT_NAME_MAX,
  DOCUMENT_PERMISSIONS,
  documentCan,
  useRenameDocument,
  useSetDocumentLabels,
  type WorkspaceDocument,
} from '@/entities/document';
import { useWorkspaces } from '@/entities/workspace';
import { DocumentAccessDialog } from '@/features/document-access';
import { ResourceLabelsDialog } from '@/features/resource-labels';
import { FormError } from '@/shared/forms';
import { Button, Dialog, DialogActions, Icons, TextField } from '@/shared/ui';

import styles from './DocumentCatalog.module.css';

/**
 * Settings of a note from its catalog card: the name (current value loaded), its labels and its
 * access. Each part is the same editor the note's own page uses and each is gated by the right the
 * server reported for the viewer, so being allowed to rename never implies sharing.
 */
export function DocumentSettingsDialog({
  document,
  onClose,
}: {
  document: WorkspaceDocument;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const rename = useRenameDocument(document.id);
  const labels = useSetDocumentLabels(document.id);
  const workspaces = useWorkspaces();
  const isTeam =
    workspaces.data?.find((w) => w.id === document.workspace_id)?.workspace_type === 'team';
  const mayEdit = documentCan(document, DOCUMENT_PERMISSIONS.editDetails);
  const mayManage = documentCan(document, DOCUMENT_PERMISSIONS.manageAccess);
  const [name, setName] = useState(document.name);
  const [part, setPart] = useState<'labels' | 'access' | null>(null);
  if (part === 'labels') {
    return (
      <ResourceLabelsDialog
        kind="document"
        title={t('resourceLabels.titleDocument', { name: document.name })}
        workspaceId={document.workspace_id}
        current={document.labels ?? []}
        pending={labels.isPending}
        error={labels.error}
        onSave={(labelIds) => {
          labels.mutate(labelIds, {
            onSuccess: () => {
              setPart(null);
            },
          });
        }}
        onClose={() => {
          setPart(null);
        }}
      />
    );
  }
  if (part === 'access') {
    return <DocumentAccessDialog document={document} isTeam={isTeam} onClose={onClose} />;
  }
  const changed = name.trim() !== document.name;
  return (
    <Dialog title={t('catalog.editResource')} onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!mayEdit || !name.trim()) return;
          if (!changed) {
            onClose();
            return;
          }
          rename.mutate(name.trim(), { onSuccess: onClose });
        }}
      >
        <TextField
          label={t('documents.name')}
          value={name}
          maxLength={DOCUMENT_NAME_MAX}
          disabled={!mayEdit}
          autoFocus
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <div className={styles.settingsLinks}>
          {mayEdit && isTeam && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPart('labels');
              }}
            >
              <Icons.tag /> {t('home.menuEditLabels')}
            </Button>
          )}
          {mayManage && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPart('access');
              }}
            >
              <Icons.key /> {t('catalog.manageAccess')}
            </Button>
          )}
        </div>
        <FormError error={rename.error} />
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={rename.isPending || !mayEdit}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
